import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, asset } from "../../lib/api";
import { uploadChunked } from "../../lib/upload";
import { Input } from "../ui/input";
import { Check, X, ArrowLeftRight, ChevronUp, ChevronDown, Trash2, Upload, Loader2, RefreshCw } from "lucide-react";

function PairRow({ p, hdr, onChange, onMove, idx, count }) {
  const [caption, setCaption] = useState(p.caption || "");
  const patch = async (body, msg) => {
    try { await axios.patch(`${API}/admin/incognito/stories/${p.id}`, body, hdr()); if (msg) toast.success(msg); onChange(); }
    catch { toast.error("Save failed"); }
  };
  const approved = p.status === "approved";
  return (
    <div data-testid={`admin-pair-${p.id}`} className={`border rounded-lg p-3 bg-obsidian-800 ${approved ? "border-amber-glow/50" : "border-white/10"}`}>
      <div className="grid grid-cols-2 gap-2">
        {[["Guests saw", p.guest_url], ["I caught", p.tele_url]].map(([l, src]) => (
          <div key={l} className="relative aspect-[4/5] bg-black rounded flex items-center justify-center overflow-hidden">
            <img src={asset(src)} alt="" loading="lazy" className="max-w-full max-h-full object-contain" />
            <span className="absolute top-1 left-1 text-[10px] bg-black/70 rounded px-1.5 py-0.5">{l}</span>
          </div>
        ))}
      </div>
      <Input data-testid={`pair-caption-${p.id}`} value={caption} placeholder="Caption (optional)" className="mt-2 text-sm"
        onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== (p.caption || "") && patch({ caption }, "Caption saved")} />
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        {approved
          ? <button data-testid={`pair-unapprove-${p.id}`} onClick={() => patch({ status: "suggested" }, "Removed from page")} className="border border-white/15 rounded-full px-3 py-1 hover:border-white/40">Take off page</button>
          : <button data-testid={`pair-approve-${p.id}`} onClick={() => patch({ status: "approved" }, "Added to page")} className="btn-amber rounded-full px-3 py-1 flex items-center gap-1"><Check size={12} /> Approve</button>}
        <button data-testid={`pair-swap-${p.id}`} onClick={() => patch({ swap: true })} className="border border-white/15 rounded-full px-3 py-1 flex items-center gap-1 hover:border-white/40"><ArrowLeftRight size={12} /> Swap sides</button>
        <button data-testid={`pair-reject-${p.id}`} onClick={() => patch({ status: "rejected" }, "Pair discarded")} className="text-neutral-500 hover:text-red-400 flex items-center gap-1"><X size={12} /> Discard</button>
        {approved && (
          <span className="ml-auto flex gap-1">
            <button data-testid={`pair-up-${p.id}`} disabled={idx === 0} onClick={() => onMove(idx, idx - 1)} className="disabled:opacity-20"><ChevronUp size={16} /></button>
            <button data-testid={`pair-down-${p.id}`} disabled={idx === count - 1} onClick={() => onMove(idx, idx + 1)} className="disabled:opacity-20"><ChevronDown size={16} /></button>
          </span>
        )}
      </div>
    </div>
  );
}

function Pairs({ hdr }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/admin/incognito/stories`, hdr()).then(({ data }) => setItems(data)).catch(() => {}), [hdr]);
  useEffect(() => { load(); }, [load]);
  const approved = items.filter((p) => p.status === "approved");
  const suggested = items.filter((p) => p.status === "suggested");
  const move = async (from, to) => {
    const ids = approved.map((p) => p.id);
    const [x] = ids.splice(from, 1);
    ids.splice(to, 0, x);
    await axios.post(`${API}/admin/incognito/stories/reorder`, { ids }, hdr());
    load();
  };
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-2xl font-light">Real-Moment Stories</h3>
          <p className="text-neutral-500 text-sm mt-1">AI paired a wide "guest view" with a long-lens moment from the same wedding. Approve the ones you like — only approved pairs show on the Incognito page.</p>
        </div>
        <button onClick={load} className="text-sm text-neutral-400 flex items-center gap-1 hover:text-white"><RefreshCw size={13} /> Refresh</button>
      </div>
      <div>
        <div className="overline-label mb-3 text-amber-glow">On the page ({approved.length})</div>
        {approved.length === 0 && <div className="text-neutral-500 text-sm">None yet — approve a suggestion below.</div>}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {approved.map((p, i) => <PairRow key={p.id} p={p} hdr={hdr} onChange={load} onMove={move} idx={i} count={approved.length} />)}
        </div>
      </div>
      <div>
        <div className="overline-label mb-3">AI suggestions ({suggested.length})</div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {suggested.map((p) => <PairRow key={p.id} p={p} hdr={hdr} onChange={load} />)}
        </div>
      </div>
    </div>
  );
}

function AssetUploader({ kind, hdr, onDone }) {
  const [file, setFile] = useState(null);
  const [outlet, setOutlet] = useState("");
  const [title, setTitle] = useState("");
  const [pct, setPct] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const go = async () => {
    if (!file) return;
    setPct(0);
    try {
      const headers = hdr().headers;
      const url = await uploadChunked(file, headers, setPct);
      await axios.post(`${API}/admin/incognito/assets`, { kind, url, outlet, title }, { headers });
      toast.success(kind === "clip" ? "Clip added" : "Article added");
      setFile(null); setOutlet(""); setTitle(""); setInputKey((k) => k + 1); onDone();
    } catch { toast.error("Upload failed"); }
    setPct(null);
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input key={inputKey} data-testid={`asset-file-${kind}`} type="file" accept={kind === "clip" ? "video/mp4,video/quicktime" : "image/jpeg,image/png,image/webp"}
        onChange={(e) => setFile(e.target.files[0] || null)} className="text-sm text-neutral-400" />
      {kind === "press" && (
        <>
          <Input data-testid="press-outlet" placeholder="Publication (e.g. Las Vegas Sun)" value={outlet} onChange={(e) => setOutlet(e.target.value)} className="w-56 text-sm" />
          <Input data-testid="press-title" placeholder="Headline" value={title} onChange={(e) => setTitle(e.target.value)} className="w-72 text-sm" />
        </>
      )}
      <button data-testid={`asset-upload-${kind}`} onClick={go} disabled={!file || pct !== null}
        className="btn-amber rounded-full px-4 py-2 text-xs flex items-center gap-1.5 disabled:opacity-50">
        {pct !== null ? <><Loader2 size={12} className="animate-spin" /> {pct}%</> : <><Upload size={12} /> Upload</>}
      </button>
    </div>
  );
}

function Assets({ hdr }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/incognito/assets`).then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await axios.delete(`${API}/admin/incognito/assets/${id}`, hdr()); load(); };
  const section = (kind, title, help) => (
    <div className="border border-white/10 rounded-lg p-5 bg-obsidian-800 space-y-4" data-testid={`admin-assets-${kind}`}>
      <div>
        <h3 className="font-serif text-xl font-light">{title}</h3>
        <p className="text-neutral-500 text-xs mt-1">{help}</p>
      </div>
      <AssetUploader kind={kind} hdr={hdr} onDone={load} />
      <div className="flex flex-wrap gap-3">
        {items.filter((a) => a.kind === kind).map((a) => (
          <div key={a.id} className="relative w-32">
            {kind === "clip"
              ? <video src={asset(a.url)} muted className="w-32 h-56 object-cover rounded bg-black" />
              : <img src={asset(a.url)} alt="" className="w-32 h-40 object-cover rounded" />}
            <button data-testid={`asset-delete-${a.id}`} onClick={() => del(a.id)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1.5 text-red-300 hover:text-red-400"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {section("clip", "Social clips", "Short vertical videos (MP4 or MOV). They play in phone frames on the Incognito page. Until you add some, sample frames show 'Clip coming soon'.")}
      {section("press", "Press feature", "Upload a photo or scan of the article (e.g. Las Vegas Sun with Don King). It appears in 'Who you're hiring'.")}
    </div>
  );
}

export function IncognitoStoriesAdmin({ hdr }) {
  return (
    <div className="space-y-14" data-testid="admin-incognito-stories">
      <Assets hdr={hdr} />
      <Pairs hdr={hdr} />
    </div>
  );
}
